import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  useSupport,
  initSupport,
  openSession,
  closeSession,
  selectBadgeCount,
} from "../../hooks/supportStore";
import SupportInbox from "./support-inbox";
import SupportChat from "./support-chat";
import SupportCustomerCard from "./support-customer-card";
import SupportAgentsAdmin from "./support-agents-admin";
import "./support.css";

const NARROW_QUERY = "(max-width: 900px)";

function useIsNarrow() {
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches);
  useEffect(() => {
    const media = window.matchMedia(NARROW_QUERY);
    const onChange = (e) => setNarrow(e.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);
  return narrow;
}

function SupportIndex() {
  const support = useSupport();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("session");
  const view = params.get("view");
  const narrow = useIsNarrow();
  const [showDetails, setShowDetails] = useState(false);
  const badge = selectBadgeCount(support);

  useEffect(() => {
    initSupport();
  }, []);

  useEffect(() => {
    if (!selectedId || !support.enabled) return undefined;
    openSession(selectedId);
    return () => closeSession(selectedId);
  }, [selectedId, support.enabled]);

  useEffect(() => {
    const base = "Customer Support | BuildHub Chat";
    document.title = badge > 0 ? `(${badge}) ${base}` : base;
  }, [badge]);

  const open = (id) => {
    setShowDetails(false);
    setParams({ session: id });
  };
  const showAdmin = () => setParams({ view: "agents" });
  const backToInbox = () => setParams({});

  if (!support.initialized) return <div className="cs-shell cs-shell--center">Loading customer support…</div>;

  const isAdmin = Boolean(support.me?.admin_access);
  if (!support.enabled && !isAdmin) {
    return (
      <div className="cs-shell cs-shell--center">
        <p>Customer support is only available to support agents.</p>
        <Link className="cs-btn cs-btn--primary" to="/">
          Back to chat
        </Link>
      </div>
    );
  }

  if (view === "agents" || !support.enabled) {
    return (
      <div className="cs-shell">
        <SupportAgentsAdmin onClose={support.enabled ? backToInbox : null} />
      </div>
    );
  }

  const chat = selectedId ? (
    <SupportChat
      sessionId={selectedId}
      onBack={narrow ? backToInbox : null}
      onShowDetails={narrow ? () => setShowDetails(true) : null}
    />
  ) : (
    <div className="cs-chat cs-chat--empty">
      <i className="fa fa-comments-o" aria-hidden />
      <p>Select a customer from the queue to start helping.</p>
    </div>
  );

  if (narrow) {
    return (
      <div className="cs-shell cs-shell--narrow">
        {!selectedId && <SupportInbox selectedId={selectedId} onOpen={open} onShowAdmin={showAdmin} />}
        {selectedId && !showDetails && chat}
        {selectedId && showDetails && (
          <SupportCustomerCard sessionId={selectedId} onClose={() => setShowDetails(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="cs-shell">
      <SupportInbox selectedId={selectedId} onOpen={open} onShowAdmin={showAdmin} />
      {chat}
      {selectedId && <SupportCustomerCard sessionId={selectedId} />}
    </div>
  );
}

export default SupportIndex;
